/**
 * Roles are **data**, not a fixed union.
 *
 * A workspace defines its own roles through the backend's RBAC screens, so
 * `Role` is a string code and the four below are only the ones every workspace
 * is provisioned with. Never branch on a role code in the UI — branch on a
 * permission (`useAuthStore().can(...)`), which is what the backend enforces too.
 */
export const ROLES = ['ADMIN', 'RECEIVER', 'DISPATCHER', 'VIEWER'] as const;
export type BuiltInRole = (typeof ROLES)[number];

/** A role code as issued by the backend. Any workspace-defined string is valid. */
export type Role = string;

/** Fallback labels for the built-in roles; the API sends `roleName` for the rest. */
export const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrator',
  RECEIVER: 'Warehouse Receiver',
  DISPATCHER: 'Warehouse Dispatcher',
  VIEWER: 'Viewer',
};

/**
 * Turns a role code into something worth showing a human: the name the backend
 * sent, else a built-in label, else a tidied-up code ("QC_INSPECTOR" → "Qc Inspector").
 */
export function roleLabel(role: string | undefined, roleName?: string): string {
  if (roleName) return roleName;
  if (!role) return '—';
  return (
    ROLE_LABELS[role] ??
    role
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (character) => character.toUpperCase())
  );
}

/**
 * Permission codes the UI branches on. Mirrors `Permissions.java`.
 *
 * The array is `const` for autocomplete, but `Permission` stays assignable from
 * any string: the backend can ship a new permission without this bundle needing
 * a release to understand it.
 */
export const PERMISSIONS = [
  'DASHBOARD_VIEW',
  'QR_VIEW',
  'QR_GENERATE',
  'QR_CANCEL',
  'QR_PRINT',
  'CARTON_VIEW',
  'CARTON_RECEIVE',
  'CARTON_DISPATCH',
  'SCAN_HISTORY_VIEW',
  'RECEIVED_LOT_VIEW',
  'RECEIVED_LOT_MANAGE',
  'DISPATCHED_LOT_VIEW',
  'COMMERCIAL_INFO_VIEW',
  /** Enter a lot's commercial info for the first time (Commercial role). */
  'COMMERCIAL_INFO_CREATE',
  /** Change commercial info that has already been entered (workspace admin). */
  'COMMERCIAL_INFO_EDIT',
  'BUYER_VIEW',
  'BUYER_MANAGE',
  'PURCHASE_ORDER_VIEW',
  'PURCHASE_ORDER_MANAGE',
  'STYLE_VIEW',
  'STYLE_MANAGE',
  'LOT_NUMBER_VIEW',
  'LOT_NUMBER_MANAGE',
  'SIZE_VIEW',
  'SIZE_MANAGE',
  'WAREHOUSE_MANAGE',
  'SHIPMENT_MANAGE',
  'REPORT_VIEW',
  'REPORT_EXPORT',
  'USER_VIEW',
  'USER_MANAGE',
  'ROLE_VIEW',
  'ROLE_MANAGE',
  'NAVIGATION_MANAGE',
  'SETTINGS_VIEW',
  'SETTINGS_MANAGE',
  /** Platform operators only — never granted to a workspace role. */
  'TENANT_VIEW',
  'TENANT_MANAGE',
] as const;

export type KnownPermission = (typeof PERMISSIONS)[number];
export type Permission = KnownPermission | (string & {});

export type UserStatus = 'ACTIVE' | 'INACTIVE';

/** Which scanner app a role gets on mobile. Decided by the backend, not the bundle. */
export type MobilePersona = 'NONE' | 'RECEIVE' | 'DISPATCH';

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  /** Primary role code — decides the landing route and scanner persona. */
  role: Role;
  /** Human label for the primary role, e.g. "QC Inspector". */
  roleName?: string;
  /** Every role code held, primary first. */
  roles?: Role[];
  status: UserStatus;
  phone?: string;
  avatarUrl?: string;
  warehouseId?: number;
  warehouseName?: string;
  lastLoginAt?: string;
  mustChangePassword?: boolean;

  /* ---------------------------- SaaS additions ---------------------------- */

  /** Workspace this account belongs to. */
  tenantId?: number;
  tenantSlug?: string;
  tenantName?: string;
  /** Effective capabilities. The only thing the UI should gate on. */
  permissions?: Permission[];
  /** Where this user lands after signing in — configured per role, server-side. */
  landingRoute?: string;
  mobilePersona?: MobilePersona;
}

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
  /** Workspace slug. Only needed when one email exists in several workspaces. */
  tenant?: string;
}

/** Matches a typical JWT response from `AuthController#login`. */
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: 'Bearer';
  /** seconds */
  expiresIn: number;
  user: AuthUser;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateProfileRequest {
  name?: string;
  phone?: string;
  avatarUrl?: string;
}
